/** Нижняя навигация: каталог, игры, аккаунт.
 *
 * Боковые кнопки у правого края были компромиссом — они не мешали каталогу,
 * но и не читались как навигация: человек не знает, что приложение состоит из
 * трёх частей, пока не наткнётся на торчащий ярлык. Полоса внизу говорит это
 * сразу и стоит там, где её ищут по привычке.
 *
 * Форма — плавающая стеклянная капсула: она лежит поверх страницы, а не
 * отрезает от неё нижнюю полосу. Активную вкладку показывает линза, и она
 * здесь не метка, а предмет: её можно взять пальцем и провести вдоль полосы.
 * Линза едет за пальцем, у каждой вкладки щёлкает отдачей, как деление
 * барабана, и на отпускании встаёт на ближайшую. Это и отличает стекло от
 * нарисованного прямоугольника — на него можно нажать, а взять нельзя.
 *
 * При протяжке линза чуть вытягивается вдоль движения и расправляется, когда
 * встала. Растяжение — единственная вольность: положение никуда не
 * отскакивает, потому что во всём остальном интерфейсе движение гасится
 * плавно, и один пружинящий элемент читался бы как чужой.
 */
import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import type { Catalog, GamesView, Reward } from '@/shared/types';
import { pick } from '@/shared/tg';
import { TicketGlyph } from './icons';
import type { Tab } from './nav';

interface Item {
  tab: Tab;
  label: string;
  icon: React.ReactNode;
  /** Живая метка: игра доступна, награда не потрачена */
  dot?: boolean;
  /** Цифра на вкладке: сколько штук в корзине */
  count?: number;
}

/** Сколько пикселей ждём, прежде чем счесть касание протяжкой, а не тапом. */
const DECIDE = 5;
/** Насколько линза вытягивается на быстром движении. */
const STRETCH = 0.14;

const Catalog = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"
       strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="2" />
    <rect x="14" y="3" width="7" height="7" rx="2" />
    <rect x="3" y="14" width="7" height="7" rx="2" />
    <rect x="14" y="14" width="7" height="7" rx="2" />
  </svg>
);

const Games = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"
       strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="2.4" />
    <path d="M12 3v6M12 15v6M3 12h6M15 12h6" />
  </svg>
);

const Cart = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"
       strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 6h2l2.4 10.2a1.6 1.6 0 0 0 1.6 1.3h7.1a1.6 1.6 0 0 0 1.6-1.2L20.5 9H7" />
    <circle cx="10.5" cy="20.5" r="1.3" />
    <circle cx="17.5" cy="20.5" r="1.3" />
  </svg>
);

const Account = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"
       strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="3.6" />
    <path d="M4.5 20c1.2-3.6 4-5.4 7.5-5.4s6.3 1.8 7.5 5.4" />
  </svg>
);

/** Какие вкладки сейчас есть. Нужно и полосе внизу, и жесту листания:
    оба должны считать соседей одинаково. */
export function tabsOf(games: GamesView | null, rewards: Reward[],
                       cartCount = -1, raffle: Catalog['raffle'] = null): Item[] {
  const items: Item[] = [{ tab: 'catalog', label: 'Каталог', icon: <Catalog /> }];
  // Раздел игр показываем, только если он вообще включён: пустая вкладка
  // хуже отсутствующей
  if (games?.enabled) {
    items.push({
      tab: 'games',
      label: games.button_text || 'Игры',
      icon: <Games />,
      dot: !games.teaser && games.games.some((game) => game.wait === 0),
    });
  }
  // Корзина есть только в корзинном режиме; −1 — магазин работает заявками
  if (cartCount >= 0) {
    items.push({ tab: 'cart', label: 'Корзина', icon: <Cart />,
                 count: cartCount || undefined });
  }
  // Розыгрыш — только пока он идёт: вкладка без приза в игре была бы
  // витриной пустоты. Точка — «у тебя есть билеты в игре»
  if (raffle) {
    items.push({ tab: 'raffle', label: 'Розыгрыш', icon: <TicketGlyph weight={1.9} />,
                 dot: raffle.tickets > 0 });
  }
  items.push({
    tab: 'account',
    label: 'Аккаунт',
    icon: <Account />,
    dot: rewards.length > 0,
  });
  return items;
}

export function TabBar({
  tab,
  onOpen,
  games,
  rewards,
  cartCount = -1,
  raffle = null,
}: {
  tab: Tab;
  onOpen: (tab: Tab) => void;
  games: GamesView | null;
  rewards: Reward[];
  cartCount?: number;
  raffle?: Catalog['raffle'];
}) {
  const items = tabsOf(games, rewards, cartCount, raffle);
  const at = Math.max(0, items.findIndex((item) => item.tab === tab));

  const bar = useRef<HTMLElement>(null);
  const lens = useRef<HTMLSpanElement>(null);
  /** Куда указывает линза прямо сейчас: во время протяжки это ещё не выбор */
  const [aiming, setAiming] = useState<number | null>(null);
  const hand = useRef<
    { x: number; from: number; moving: boolean; pos: number; last: number } | null
  >(null);
  /** Протяжка кончается кликом по кнопке под пальцем — его надо съесть */
  const dragged = useRef(false);

  /** Ширина одной вкладки: шаг, которым живёт линза. */
  const step = () => {
    const box = bar.current?.getBoundingClientRect();
    return box ? (box.width - 12) / items.length : 0;
  };

  const place = (x: number, stretch: number) => {
    const node = lens.current;
    if (!node) return;
    node.style.transition = 'none';
    node.style.transform = `translate3d(${x}px, 0, 0) scaleX(${1 + stretch})`;
  };

  const onDown = (event: React.PointerEvent) => {
    // Тащить можно только саму линзу — как в iOS. Нажатие мимо остаётся
    // нажатием: иначе палец, соскользнувший с чужой вкладки, утаскивал бы
    // выделение оттуда, где его не брали
    const box = bar.current?.getBoundingClientRect();
    const width = step();
    if (!box || !width) return;
    const cell = Math.floor((event.clientX - box.left - 6) / width);
    if (cell !== at) return;

    hand.current = { x: event.clientX, from: at, moving: false, pos: at * width, last: at };
  };

  const onMove = (event: React.PointerEvent) => {
    const hold = hand.current;
    if (!hold) return;
    const dx = event.clientX - hold.x;
    if (!hold.moving && Math.abs(dx) < DECIDE) return;

    if (!hold.moving) {
      hold.moving = true;
      // Захват берём здесь, а не на нажатии: взятый сразу, он ломал бы тапы
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        /* захват — удобство, а не условие работы */
      }
      setAiming(hold.from);
    }

    const width = step();
    if (!width) return;
    const limit = (items.length - 1) * width;
    const moved = Math.min(limit, Math.max(0, hold.from * width + dx));
    // Вытягивание вдоль движения: чем быстрее ведут, тем сильнее — и сразу
    // расправляется, стоит пальцу замедлиться
    const speed = Math.abs(moved - hold.pos);
    hold.pos = moved;
    place(moved, Math.min(STRETCH, speed / 90));

    const near = Math.round(moved / width);
    if (near !== hold.last) {
      hold.last = near;
      pick();
      setAiming(near);
    }
  };

  const onUp = () => {
    const hold = hand.current;
    hand.current = null;
    if (!hold) return;

    if (!hold.moving) {
      // Не тащили, а нажали — обычный тап по вкладке под пальцем
      setAiming(null);
      return;
    }

    // Клик придёт следом за отпусканием и попадёт в кнопку под пальцем.
    // Для протяжки это уже лишнее: вкладку мы выбрали сами
    dragged.current = true;
    window.setTimeout(() => {
      dragged.current = false;
    }, 150);

    const target = items[hold.last] ?? items[hold.from]!;
    // Сначала синхронно ставим вкладку, потом отпускаем линзу: она поедет к
    // новому месту сама, обычным переходом
    flushSync(() => {
      setAiming(null);
      if (hold.last !== hold.from) onOpen(target.tab);
    });

    const node = lens.current;
    if (!node) return;
    node.style.transition = '';
    // Заставляем браузер запомнить, откуда ехать: без этого смена transition и
    // transform в один приём иногда проходит рывком
    void node.offsetWidth;
    node.style.transform = '';
  };

  return (
    <nav
      ref={bar}
      className="tabbar"
      style={{ '--tabs': items.length, '--at': at } as React.CSSProperties}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
    >
      <span ref={lens} className="tabbar-lens" aria-hidden />
      {items.map((item, index) => {
        // Во время протяжки подсвечена та, к которой линза сейчас ближе:
        // выбор виден до того, как палец отпустили
        const active = aiming === null ? tab === item.tab : aiming === index;
        return (
          <button
            key={item.tab}
            type="button"
            aria-current={active ? 'page' : undefined}
            onClick={() => {
              if (dragged.current) return;
              onOpen(item.tab);
            }}
            className={`tabbar-item ${active ? 'on' : ''}`}
          >
            <span className="ico">
              {item.icon}
              {item.dot && <i className="dot" />}
              {item.count !== undefined && (
                <i className="absolute -top-1 -right-2 grid min-w-[15px] place-items-center rounded-full bg-mint px-0.5 text-[9.5px] leading-[15px] font-[700] not-italic text-mint-ink">
                  {item.count > 99 ? '99' : item.count}
                </i>
              )}
            </span>
            <span className="txt">{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
